import type { QueryClient } from '@tanstack/react-query';

/**
 * Multi-operator concurrency: when two operators click the same action
 * on the same draft within milliseconds, the loser's request hits the
 * publishing-service's status-guarded UPDATE and gets HTTP 409 back —
 * carrying the winner's identity so we can show an accurate message
 * instead of a generic "something went wrong".
 *
 * Two payload shapes need to be tolerated:
 *
 * 1. Social drafts (publishing-service → backend pass-through):
 *      { detail: { error: { code, message, statusCode }, current: {...} } }
 *    (backend wraps the upstream body inside FastAPI's `detail`)
 *
 * 2. Notification drafts (publishing-service ApprovalResult shape):
 *      { detail: { ok: false, error: 'draft_already_claimed',
 *                  error_detail: 'already claimed by <actor> at <time>',
 *                  draft_id, sent_count: 0, ... } }
 *
 * `handleMutationConflict` recognises both, shows a short alert, and
 * invalidates the supplied query keys so the UI refreshes to the
 * winner's state. Returns true if the error was a 409 (caller should
 * suppress any other error UX); false otherwise (caller handles normally).
 */
export function handleMutationConflict(
  error: any,
  queryClient: QueryClient,
  invalidateKeys: readonly (readonly unknown[])[],
): boolean {
  const status = error?.response?.status;
  if (status !== 409) return false;

  const detail = error?.response?.data?.detail ?? error?.response?.data;
  let message = 'Another operator already actioned this draft.';

  // Not every 409 is a race. The backend's ConflictError handler returns
  // { error: 'Conflict', detail: '<reason>' } — e.g. the QG fix preview
  // rejecting an edit pass that leaked deliberation text. Show the reason
  // rather than blaming a phantom operator.
  if (typeof detail === 'string' && detail.trim()) {
    message = detail;
  }

  // Notifications: ApprovalResult shape
  if (detail && typeof detail === 'object' && 'error' in detail && typeof detail.error === 'string') {
    if (detail.error === 'draft_already_claimed') {
      message = `Already actioned: ${detail.error_detail ?? 'another operator beat you to it'}.`;
    } else if (detail.error === 'draft_not_pending') {
      message = `Draft is no longer pending approval: ${detail.error_detail ?? 'state changed'}.`;
    } else if (typeof detail.error_detail === 'string') {
      message = detail.error_detail;
    }
  }

  // Social: { error: {code, message}, current: {status, posted_by, posted_at, ...} }
  // Article: { message, current: {updated_at, edited_by, edited_at, publish_status} }
  if (detail && typeof detail === 'object' && 'current' in detail && detail.current) {
    const c = detail.current;
    // Article / notification-draft shape: updated_at + edited_by/edited_at.
    // A notification draft carries `status`; an article carries `publish_status`.
    if (typeof c.updated_at === 'string' && (c.edited_by !== undefined || c.edited_at !== undefined)) {
      const actor = (typeof c.edited_by === 'string' && c.edited_by)
        ? c.edited_by.split('@')[0]
        : 'another operator';
      const noun = ('status' in c && !('publish_status' in c)) ? 'This notification' : 'Article';
      message = `${noun} was edited by ${actor}. Your changes were NOT saved — refresh to see the latest version.`;
    } else {
      // Social shape (status + posted_by/skipped_at).
      const actor = c.posted_by ?? 'another operator';
      const at = c.posted_at ?? c.skipped_at ?? null;
      if (c.status === 'posted') {
        message = `Already posted${actor ? ` by ${actor}` : ''}${at ? ` at ${at}` : ''}.`;
      } else if (c.status === 'skipped') {
        message = `Already hidden${at ? ` at ${at}` : ''}.`;
      } else {
        message = `Draft is now in status '${c.status}'; refresh and try again.`;
      }
    }
  }

  // Article: backend uses HTTPException(detail={"message": ..., "current": ...})
  // — when there's no inner `error` field, fall back to `detail.message`.
  if (
    detail &&
    typeof detail === 'object' &&
    typeof (detail as any).message === 'string' &&
    !('error' in detail)
  ) {
    // Only override if we haven't already produced an article-specific
    // message above (the article-current branch sets a better one).
    if (
      message === 'Another operator already actioned this draft.' ||
      !('current' in detail)
    ) {
      message = (detail as any).message;
    }
  }

  // Surface to operator. Plain `alert` matches the rest of the manager
  // (no toast library imported); replace when one lands.
  // eslint-disable-next-line no-alert
  window.alert(message);

  // Always refresh so the UI catches up to the winner's state.
  for (const key of invalidateKeys) {
    queryClient.invalidateQueries({ queryKey: key as unknown[] });
  }

  return true;
}
